import React, { useEffect, useState } from 'react';
import { Title, Flex, Body } from '@cognite/cogs.js';
import { Icon } from 'components/Icon';
import { Table } from 'antd';
import { Role } from 'types/dt-graphql-apollo-v2';
import { useGetPermissions } from 'hooks/useAccessControl';
import { Permission } from 'store/permissions';
import mixpanel from 'utils/mixpanel-frontend';
import { useUrl } from 'hooks/useUrls';
import { ColumnsType } from 'antd/lib/table';

const actionsLabels = ['Feature', 'Create', 'Read', 'Update', 'Delete'];
const targetLabels = [
  'User roles',
  'Project',
  'Schema',
  'Collection',
  'Ruleset',
  'Rule',
  'Run',
];

/** Replace any new terminology with the terms used in the data-model. */
const adaptTerminology = (permission: string) =>
  permission
    .replace('User roles', 'Access')
    .replace('Collection', 'Milestone')
    .replace('Schema', 'SchemaCollection')
    .replace('Project', 'DigitalTwin')
    .replace('Run', 'RuleRunStatus');

/** Get an overall description for a given Role. */
const getRoleInformation = (role: Role | null) => {
  switch (role) {
    case Role.Editor:
      return 'Work access level, manage tools inside the project.';
    case Role.Admin:
      return 'Administrative access level, manage project and role specific content.';
    default:
      return 'Limited access level, read content only.';
  }
};

type AccessControlMatrixProps = {
  role: Role | null;
};

export const AccessControlMatrix = ({ role }: AccessControlMatrixProps) => {
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const tenant = useUrl.Tenant();

  const { getPermissions, fetchingPermissions } = useGetPermissions();

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!role) return;

      const permissionsFetched = await getPermissions(role);
      setPermissions(permissionsFetched || []);
      mixpanel.track(`Inquired more details about the role ${role}.`, {
        tenant,
        role,
      });
    };

    fetchPermissions();
  }, [role]);

  // Create a matrix using the targetLabels and the permissions fetched
  const accessMatrix = targetLabels.map((x) => {
    const target = adaptTerminology(x);

    return {
      Feature: x,
      Create: permissions.includes(`Create_${target}` as Permission),
      Delete: permissions.includes(`Delete_${target}` as Permission),
      Read: permissions.includes(`list${target}` as Permission),
      Update: permissions.includes(`Update_${target}` as Permission),
    };
  });

  const renderField = (fieldValue: any) => {
    if (targetLabels.includes(fieldValue)) {
      return <div>{fieldValue}</div>;
    }
    if (fieldValue) {
      // Label missing in v9 → using Icon only
      return <Icon type="Checkmark" size={20} />;
    }
    return null;
  };

  // Add columns for all the possible CRUD actions
  const tableColumns: ColumnsType<Object> = actionsLabels.map(
    (actionLabel) => ({
      align: 'center',
      title: <div>{actionLabel}</div>,
      key: actionLabel,
      dataIndex: actionLabel,
      width: 200,
      render: (fieldValue: any) => renderField(fieldValue),
    })
  );

  return (
    <Flex direction="column" gap={15} style={{ padding: '1rem' }}>
      <Title level={5}>{role?.replace('Viewer', 'Reader')}</Title>
      <Body level={5}>{getRoleInformation(role)}</Body>
      <Table
        bordered
        loading={fetchingPermissions}
        dataSource={accessMatrix}
        columns={tableColumns}
        pagination={false}
      />
    </Flex>
  );
};
